import { Injectable } from '@angular/core';
import { Observable, of, throwError, BehaviorSubject, interval } from 'rxjs';
import { delay, takeWhile, map, finalize, switchMap } from 'rxjs/operators';


export interface ValidationResult {
  isValid: boolean;
  totalRecords: number;
  errors: string[];
  summary?: string;
  headers?: string[];
  sampleData?: any[];
  // New: per-record validation results for table rendering
  records?: Array<{
    row: number;
    status: 'success' | 'failed';
    message?: string;
    data: Record<string, any>;
  }>;
}

export interface ProcessResponse {
  status: 'processing' | 'completed';
  processedRecords: number;
  totalRecords: number;
  progress?: ImportProgress;
  errors?: any[];
  message?: string;
}

export interface ImportProgress {
  status: 'processing' | 'completed' | 'failed' | 'idle' | 'cancelled' | 'error';
  processed: number;
  total: number;
  message?: string;
  errors?: any[];
}

@Injectable({
  providedIn: 'root'
})
export class MockImportService {


  private readonly MOCK_DELAY = 1000; // Simulate network delay
  private readonly PROCESSING_INTERVAL = 500; // Time between progress updates in ms
  private readonly RECORDS_PER_UPDATE = 10; // Number of records to process per interval

  validateFile(file: File): Observable<ValidationResult> {
    // Simulate server-side validation of the full file with per-record results
    const headers = [
      'meterId', 'meterName', 'meterType', 'location', 'serialNumber',
      'make', 'model', 'installDate', 'status', 'notes'
    ];
    const total = 100;
    const records: Array<{ row: number; status: 'success' | 'failed'; message?: string; data: Record<string, any> }> = Array.from({ length: total }).map((_, idx) => {
      const failed = Math.random() < 0.15; // 15% fail rate for demo
      const data: Record<string, any> = {
        meterId: `MTR-${(10001 + idx).toString().padStart(5, '0')}`,
        meterName: `Zone ${Math.floor(idx / 10) + 1} - Meter ${idx + 1}`,
        meterType: ['Gas', 'Electric', 'Water'][idx % 3],
        location: ['North Wing', 'South Wing', 'Main Hall'][idx % 3],
        serialNumber: `SN-${idx + 1000}`,
        make: ['Acme', 'Globex', 'Initech'][idx % 3],
        model: `MDL-${(idx % 20) + 1}`,
        installDate: `2024-0${(idx % 9) + 1}-15`,
        status: 'Active',
        notes: idx % 5 === 0 ? 'Periodic inspection due' : ''
      };
      return {
        row: idx + 1,
        status: failed ? 'failed' : 'success',
        message: failed ? 'Invalid meterId format' : 'OK',
        data
      };
    });

    const result: ValidationResult = {
      isValid: true,
      totalRecords: total,
      errors: [],
      summary: 'File validated successfully',
      headers,
      sampleData: records.slice(0, 2).map(r => r.data),
      records
    };

    return of(result).pipe(delay(this.MOCK_DELAY));
  }

  processFile(file: File, importType: string): Observable<ProcessResponse> {
    const totalRecords = 100; // Mock total records
    let processedRecords = 0;
    
    return interval(this.PROCESSING_INTERVAL).pipe(
      takeWhile(() => processedRecords < totalRecords),
      map(() => {
        // Process a chunk of records
        processedRecords = Math.min(processedRecords + this.RECORDS_PER_UPDATE, totalRecords);
        const isComplete = processedRecords >= totalRecords;
        
        // Simulate occasional errors (10% chance)
        if (Math.random() < 0.1 && processedRecords > 10) {
          throw new Error(`Error processing records ${processedRecords - 5} to ${processedRecords}`);
        }
        
        return {
          status: isComplete ? 'completed' : 'processing',
          processedRecords,
          totalRecords,
          message: isComplete 
            ? 'Import completed successfully' 
            : `Processed ${processedRecords} of ${totalRecords} records`
        };
      })
    );
  }

  importChunk(data: FormData): Observable<ProcessResponse> {
    const start = parseInt(data.get('start')?.toString() || '0', 10);
    const end = parseInt(data.get('end')?.toString() || '0', 10);
    const totalRecords = parseInt(data.get('totalRecords')?.toString() || '0', 10);
    
    const isComplete = end >= totalRecords;
    const status: 'processing' | 'completed' = isComplete ? 'completed' : 'processing';
    const message = isComplete 
      ? 'Import completed successfully' 
      : `Processed ${end} of ${totalRecords} records`;
    
    // Simulate processing with a delay
    const response: ProcessResponse = {
      status,
      processedRecords: end,
      totalRecords,
      message,
      progress: {
        status: isComplete ? 'completed' : 'processing',
        processed: end,
        total: totalRecords,
        message
      }
    };
    
    return of(response).pipe(delay(500));
  }

  processChunk(data: any): Observable<any> {
    // Simulate random success/failure (80% success rate)
    const isSuccess = Math.random() > 0.2;
    
    if (isSuccess) {
      return of({
        success: true,
        processed: data.length,
        errors: []
      }).pipe(delay(500));
    } else {
      return throwError(() => ({
        error: {
          message: 'Simulated processing error',
          errors: [
            { 
              row: Math.floor(Math.random() * data.length),
              field: 'meterId',
              message: 'Invalid meter ID format'
            }
          ]
        }
      })).pipe(delay(500));
    }
  }
}
